import React, { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { useLocation } from "react-router-dom";
import athletesMostRecent from "../data/athletesMostRecentBackup.json";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Slider,
  TextField,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PasswordModal from "./PasswordModal";
import { getImagePresignedUrl, uploadFile } from "../utilities/updateS3";

const S3_BASE = "https://plantbuilt.s3.us-west-2.amazonaws.com/profile-photos";

export const nameToSlug = (name) =>
  name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const getCroppedCanvas = (img, crop, outW, outH) => {
  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, crop.x, crop.y, crop.width, crop.height, 0, 0, outW, outH);
  return canvas;
};

const canvasToBlob = (canvas) =>
  new Promise((resolve) => canvas.toBlob(resolve, "image/png"));

const PhotoUpload = () => {
  const location = useLocation();
  const { userProfile, isAdmin } = location.state || {};

  const [athleteName, setAthleteName] = useState(userProfile?.name ?? "");
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [canvases, setCanvases] = useState(null);
  const [step, setStep] = useState(1); // 1=select, 2=crop, 3=upload
  const [password, setPassword] = useState("");
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null); // { success, urls, error }

  const onCropComplete = useCallback((_croppedArea, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setCanvases(null);
      setCroppedAreaPixels(null);
      setUploadResult(null);
      setStep(2);
    };
    reader.readAsDataURL(file);
  };

  const handleCrop = async () => {
    if (!imageSrc || !croppedAreaPixels) return;

    const img = await loadImage(imageSrc);
    const slug = nameToSlug(athleteName);

    // 1. Square crop — 960×960
    const squareCanvas = getCroppedCanvas(img, croppedAreaPixels, 960, 960);

    // 2. Full original resized to 960px wide
    const scaledHeight = Math.round(
      (img.naturalHeight / img.naturalWidth) * 960
    );
    const fullCanvas = document.createElement("canvas");
    fullCanvas.width = 960;
    fullCanvas.height = scaledHeight;
    fullCanvas.getContext("2d").drawImage(
      img, 0, 0, img.naturalWidth, img.naturalHeight, 0, 0, 960, scaledHeight
    );

    // 3. Meta composite — 1200×530
    const metaCanvas = document.createElement("canvas");
    metaCanvas.width = 1200;
    metaCanvas.height = 530;
    const metaCtx = metaCanvas.getContext("2d");

    try {
      const template = await loadImage("/meta-template.png");
      metaCtx.drawImage(template, 0, 0, 1200, 530);
    } catch {
      metaCtx.fillStyle = "#709425";
      metaCtx.fillRect(0, 0, 1200, 530);
    }
    metaCtx.drawImage(
      img,
      croppedAreaPixels.x,
      croppedAreaPixels.y,
      croppedAreaPixels.width,
      croppedAreaPixels.height,
      670, 0, 530, 530
    );

    setCanvases({
      full: { canvas: fullCanvas, name: `plantbuilt-${slug}.png` },
      square: { canvas: squareCanvas, name: `plantbuilt-${slug}-square.png` },
      meta: { canvas: metaCanvas, name: `plantbuilt-${slug}-meta.png` },
    });
    setStep(3);
  };

  const handleUpload = async () => {
    setUploading(true);
    setUploadResult(null);
    const slug = nameToSlug(athleteName);
    const files = [
      { canvas: canvases.full.canvas, name: canvases.full.name },
      { canvas: canvases.square.canvas, name: canvases.square.name },
      { canvas: canvases.meta.canvas, name: canvases.meta.name },
    ];

    try {
      // Resolve the target athlete — fetch live data first, fall back to local backup
      let allAthletes = athletesMostRecent;
      try {
        const liveRes = await fetch("https://plantbuilt.s3.us-west-2.amazonaws.com/athletes.json");
        if (liveRes.ok) allAthletes = await liveRes.json();
      } catch {}
      const targetAthlete = allAthletes.find((a) => nameToSlug(a.name) === slug);
      if (!targetAthlete) throw new Error(`No athlete found matching "${athleteName}". Check the spelling.`);

      // Upload all three images to S3 via presigned URLs
      for (const { canvas, name } of files) {
        const presignedUrl = await getImagePresignedUrl(password, name);
        const blob = await canvasToBlob(canvas);
        const res = await fetch(presignedUrl, {
          method: "PUT",
          body: blob,
          headers: { "Content-Type": "image/png" },
        });
        if (!res.ok) throw new Error(`Failed to upload ${name}`);
      }

      // Update the target athlete's profile with S3 URLs
      const updatedProfile = {
        ...targetAthlete,
        img: `${S3_BASE}/plantbuilt-${slug}.png`,
        meta: `${S3_BASE}/plantbuilt-${slug}-meta.png`,
      };
      await uploadFile(password, targetAthlete.id, JSON.stringify(updatedProfile));

      setUploadResult({
        success: true,
        urls: files.map(({ name }) => `${S3_BASE}/${name}`),
      });
    } catch (err) {
      setUploadResult({ success: false, error: err.message });
    } finally {
      setUploading(false);
      setPassword("");
    }
  };

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", p: 3 }}>
      <Typography variant="h4" fontFamily="Impact" mb={3}>
        Upload Athlete Photo
      </Typography>

      {/* Name field */}
      <TextField
        label="Athlete Name"
        value={athleteName}
        onChange={(e) => setAthleteName(e.target.value)}
        disabled={!isAdmin}
        fullWidth
        sx={{ mb: 3 }}
        helperText={isAdmin ? "Admin: enter any athlete's name" : ""}
      />

      {/* Step 1: File picker */}
      {step >= 1 && (
        <Box mb={3}>
          <Button variant="outlined" component="label">
            {imageSrc ? "Choose Different Photo" : "Select Photo"}
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={handleFileChange}
            />
          </Button>
        </Box>
      )}

      {/* Step 2: Crop */}
      {step === 2 && imageSrc && (
        <>
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: 400,
              mb: 2,
              background: "#111",
            }}
          >
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </Box>
          <Typography gutterBottom>Zoom</Typography>
          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.01}
            onChange={(_, val) => setZoom(val)}
            sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            color="success"
            onClick={handleCrop}
            disabled={!athleteName.trim()}
          >
            Generate Files
          </Button>
          {!athleteName.trim() && (
            <Typography variant="caption" color="error" display="block" mt={1}>
              Enter a name before generating files.
            </Typography>
          )}
        </>
      )}

      {/* Step 3: Upload */}
      {step === 3 && canvases && (
        <>
          <Typography variant="body1" mb={2}>
            Three files are ready to upload to S3.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 3 }}>
            {["full", "square", "meta"].map((key) => (
              <Typography key={key} variant="body2" color="text.secondary">
                {canvases[key].name}
              </Typography>
            ))}
          </Box>

          <Button
            variant="contained"
            color="success"
            startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
            onClick={() => setShowPasswordModal(true)}
            disabled={uploading}
          >
            {uploading ? "Uploading…" : "Upload to S3"}
          </Button>

          {uploadResult && (
            <Box mt={3}>
              {uploadResult.success ? (
                <Alert severity="success">
                  <Typography variant="body2" fontWeight="bold" mb={1}>
                    Upload successful!
                  </Typography>
                  {uploadResult.urls.map((url) => (
                    <Typography key={url} variant="caption" display="block">
                      {url}
                    </Typography>
                  ))}
                </Alert>
              ) : (
                <Alert severity="error">
                  Upload failed: {uploadResult.error}
                </Alert>
              )}
            </Box>
          )}

          <Button
            variant="text"
            sx={{ mt: 3, display: "block" }}
            onClick={() => {
              setStep(2);
              setCanvases(null);
              setUploadResult(null);
            }}
          >
            Back to crop
          </Button>
        </>
      )}

      <PasswordModal
        showPasswordModal={showPasswordModal}
        hideModal={() => setShowPasswordModal(false)}
        password={password}
        setPassword={setPassword}
        submitProfile={handleUpload}
      />
    </Box>
  );
};

export default PhotoUpload;
